import { refresher } from "../../../../redux/guest-slices/other.slice";
import { useDispatch, useSelector } from "react-redux";
import { btnText, messages, titles } from "./data";
import InputFieldB from "../../../ui/InputFieldB";
import Icons from "../../../../assets/js/Icons";
import { useRef, useState } from "react";

import {
  Button,
  CircularProgress,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

// Rejection actions
import { handleRejectAppointments, handleSpecialistCancelAppointment } from "../../../../redux/specialist-slices/appointments.slice";
import {  CANCEL_APPOINTMENT, REJECT_APPOINTMENT } from "../../../../utils/keywords";
import { handleCancelAppointment } from "../../../../redux/member-slices/appointment.slice";
import { checkUserType } from "../../../../utils/helper";

function Reject({ handleClose }) {
  const input = useRef("");
  const [helperText, setHelperText] = useState({});
  const dispatch = useDispatch();
  const { data, loadings, type } = useSelector((state) => state.otherSlice);

  const isSpecialist = checkUserType() === "specialist";
  const isMemberCancel = type === CANCEL_APPOINTMENT && !isSpecialist;

  const actions = {
    [REJECT_APPOINTMENT]: () =>
      handleRejectAppointments({
        id: data?.appointmentId,
        formData: { rejectReason: input?.current?.value },
      })
    ,
    [CANCEL_APPOINTMENT]: () => {
      if (isSpecialist) {
        return handleSpecialistCancelAppointment({
          id: data?.appointmentId,
          formData: { rejectReason: input?.current?.value },
        });
      }
      return handleCancelAppointment({
        id: data?.appointmentId,
        formData: { rejectReason: input?.current?.value },
      });
    },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const action = actions[type];
    if (!action) return;
    if (input?.current?.value?.trim() === "") {
      return setHelperText({ reason: ["This field is required."] });
    }

    setHelperText({});
    const success = (await dispatch(action()).unwrap()) || {};
    if (success) {
      setTimeout(() => {
        handleClose();
        dispatch(refresher());
      }, 500);
    }
  };

  if (isMemberCancel) {
    const appointmentStart = data?.startTime ? new Date(data.startTime) : null;
    const now = new Date();
    const isWithin24Hours = appointmentStart ? (appointmentStart - now) / (1000 * 60 * 60) < 24 : false;

    const handleConfirmCancel = async (e) => {
      e.preventDefault();
      setHelperText({});
      const success = (await dispatch(handleCancelAppointment({
        id: data?.appointmentId,
        formData: { rejectReason: input?.current?.value || "Cancelled by member" },
      })).unwrap()) || {};
      if (success) {
        setTimeout(() => {
          handleClose();
          dispatch(refresher());
        }, 500);
      }
    };

    return (
      <Paper>
        <DialogTitle sx={{ position: "relative" }}>
          <Typography variant="dialogTitle">
            Cancel Appointment
          </Typography>
          <IconButton size="small" variant="dialogCloseBtn" onClick={handleClose}>
            <Icons.Cross />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          className="relative"
          sx={{
            ".MuiInputBase-root": { borderRadius: "6px" },
            textarea: { resize: "none" },
          }}
        >
          <Stack spacing={2} component="form" onSubmit={handleConfirmCancel}>
            {isWithin24Hours ? (
              <Stack
                spacing={1}
                sx={{
                  p: 2,
                  border: "1px solid #ffebee",
                  borderRadius: "8px",
                  backgroundColor: "#fff5f5",
                  color: "#c62828",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Icons.Alert style={{ fill: "#c62828", width: "20px", height: "20px" }} />
                  <Typography sx={{ fontWeight: 700, fontSize: "15px" }}>
                    Non-Refundable Warning
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: "14px", lineHeight: 1.5, fontWeight: 500 }}>
                  This appointment is within 24 hours of the scheduled time. If you cancel now, your payment will not be refunded. Do you still want to continue? Learn more in our <Link component={RouterLink} to="/refund-policy" target="_blank" sx={{ color: "#c62828", textDecoration: "underline", fontWeight: 700 }}>Refund Policy</Link>.
                </Typography>
              </Stack>
            ) : (
              <Typography variant="dialogNormal">
                Are you sure you want to cancel this appointment? You are canceling more than 24 hours in advance and will receive a 100% full refund per our <Link component={RouterLink} to="/refund-policy" target="_blank" sx={{ textDecoration: "underline", fontWeight: 600 }}>Refund Policy</Link>.
              </Typography>
            )}

            <InputFieldB
              name="reason"
              label="Reason for cancellation (Optional)"
              placeholder="Provide a reason for cancellation (optional)"
              inputRef={input}
              type="textarea"
              inputtype="InputFieldB"
              helperText={helperText}
            />

            <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                onClick={handleClose}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Keep Appointment
              </Button>
              <Button
                fullWidth
                type="submit"
                color="error"
                variant="contained"
                disabled={loadings?.update}
                sx={{ textTransform: "none", fontWeight: 600 }}
                endIcon={
                  loadings?.update && (
                    <CircularProgress size={22} color="inherit" thickness={6} />
                  )
                }
              >
                Confirm Cancellation
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Paper>
    );
  }

  return (
    <Paper>
      <DialogTitle sx={{ position: "relative" }}>
        <Typography variant="dialogTitle">
          {titles?.[type] || "default title"}
        </Typography>
        <IconButton size="small" variant="dialogCloseBtn" onClick={handleClose}>
          <Icons.Cross />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        className="relative"
        sx={{
          ".MuiInputBase-root": { borderRadius: "6px" },
          textarea: { resize: "none" },
        }}
      >
        <Stack spacing={2} component="form" onSubmit={handleSubmit}>
          <Typography variant="dialogNormal">
            {messages?.[type] || "Default message"}
          </Typography>

          <InputFieldB
            name="reason"
            label="Reason"
            placeholder="Provide a reason for rejection"
            inputRef={input}
            type="textarea"
            inputtype="InputFieldB"
            helperText={helperText}
          />

          <Button
            fullWidth
            type="submit"
            color="primary"
            variant="contained"
            disabled={loadings?.update}
            endIcon={
              loadings?.update && (
                <CircularProgress size={22} color="text" thickness={6} />
              )
            }
          >
            {btnText?.[type] || "Submit"}
          </Button>
        </Stack>
      </DialogContent>
    </Paper>
  );
}

export default Reject;
